import { Info, PlugZap, Target } from "lucide-react";
import { DataTable } from "@/components/shared/data-table";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { LoadingRows } from "@/components/shared/loading-rows";
import { StatusPill } from "@/components/shared/status-badge";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { usePersonIncentive } from "@/hooks/use-person-incentive";
import {
  earnedShare,
  formatKpiValue,
  formatPercent,
  type IncentiveAwards,
  type IncentiveKpi,
  type IncentiveModifier,
  type PersonIncentive,
  totalReduction,
  yearCompleteFraction,
} from "@/lib/person-incentive";
import { cn } from "@/lib/utils";

const TITLE = "Incentives";

/**
 * Your incentive attainment for the year: how the award is built, what has been
 * taken off it, and where each KPI stands. Read live from NOS, nothing editable,
 * percentages only — there is no dollar figure in any of it.
 *
 * Shown to the person, their direct manager and People Ops; the API refuses anyone
 * else, and records a read of someone else's scorecard.
 */
export function PersonIncentiveSection({
  personId,
  personName,
  isSelf,
}: {
  personId: string;
  personName: string;
  isSelf: boolean;
}) {
  // "your" on your own profile, the person's name on someone else's.
  const whose = isSelf ? "your" : `${personName}'s`;
  // NOS1 calls these "My KPIs"; that reads wrongly on someone else's profile.
  const individualLabel = isSelf ? "My KPIs" : "Individual KPIs";
  const { data, isLoading, isError, error, refetch } = usePersonIncentive(personId);

  if (isLoading) {
    return (
      <DetailSection title={TITLE}>
        <LoadingRows />
      </DetailSection>
    );
  }

  if (isError) {
    return (
      <DetailSection title={TITLE}>
        <ErrorState
          message={
            error instanceof Error ? error.message : `Could not load ${whose} incentive scorecard`
          }
          onRetry={() => {
            void refetch();
          }}
        />
      </DetailSection>
    );
  }

  if (data && !data.connected) {
    return (
      <DetailSection title={TITLE}>
        <EmptyState
          icon={PlugZap}
          title="Not connected to NOS"
          description={`${isSelf ? "Your" : "This"} incentive is read live from NOS, and this environment has no gateway credentials. Nothing is missing from Enable — there is just nothing to read.`}
        />
      </DetailSection>
    );
  }

  const scorecard = data?.hasScorecard ? data : null;
  if (scorecard === null) {
    return (
      <DetailSection title={TITLE}>
        <EmptyState
          icon={Target}
          title={`No incentive scorecard for ${data?.year ?? "this year"} yet`}
          description={
            isSelf
              ? "NOS has no scorecard for you this year. If you expected one, ask People Ops."
              : `NOS has no scorecard for ${personName} this year.`
          }
        />
      </DetailSection>
    );
  }

  const company = scorecard.kpis.filter((k) => k.group === "company");
  const personal = scorecard.kpis.filter((k) => k.group === "personal");

  return (
    <div className="space-y-6">
      <YearProgress year={scorecard.year} />
      <AwardsBreakdown data={scorecard} individualLabel={individualLabel} />
      <KpiTable
        title="Company KPIs"
        blurb="Results for the whole company. Everyone is scored on these."
        kpis={company}
      />
      <KpiTable
        title={individualLabel}
        blurb={`Results for ${isSelf ? "your" : "their"} own team and role.`}
        kpis={personal}
      />
    </div>
  );
}

/** How far through the year we are — the yardstick for every "projected" figure below. */
function YearProgress({ year }: { year: number }) {
  const fraction = yearCompleteFraction(year);
  return (
    <div className="rounded-xl border bg-card px-5 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-base font-semibold">{year} incentive</h2>
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground tabular-nums">
            {formatPercent(fraction, 0)}
          </span>{" "}
          of the year complete
        </p>
      </div>
      <Bar fraction={fraction} tone="primary" label={`${formatPercent(fraction, 0)} of the year`} />
    </div>
  );
}

function AwardsBreakdown({
  data,
  individualLabel,
}: {
  data: PersonIncentive;
  individualLabel: string;
}) {
  const awards: IncentiveAwards = data.awards ?? {
    company: null,
    individual: null,
    base: null,
    final: null,
  };
  const reduction = totalReduction(data.modifiers);

  return (
    <DetailSection title="Awards breakdown">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Company KPIs" value={formatPercent(awards.company)} />
        <Tile label={individualLabel} value={formatPercent(awards.individual)} />
        <Tile
          label="Base award"
          value={formatPercent(awards.base)}
          hint={`Company + ${individualLabel}`}
        />
        <Tile
          label="Final award"
          value={formatPercent(awards.final)}
          hint={
            reduction > 0 ? `After ${formatPercent(reduction)} of reductions` : "After reductions"
          }
          emphasis
        />
      </div>

      <div className="mt-6">
        <h3 className="mb-2 text-sm font-semibold">Reduction modifiers</h3>
        {data.modifiersUnavailable ? (
          <p className="rounded-lg border border-warning/40 bg-warning-bg px-3 py-2 text-sm text-warning-foreground">
            The KPIs loaded, but the reductions could not be read. The final award above already
            includes them; they just can’t be itemised right now.
          </p>
        ) : (
          <ModifierList modifiers={data.modifiers} />
        )}
      </div>
    </DetailSection>
  );
}

function Tile({
  label,
  value,
  hint,
  emphasis = false,
}: {
  label: string;
  value: string;
  hint?: string;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border px-4 py-3",
        emphasis ? "border-primary/30 bg-accent" : "bg-card",
      )}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-1 font-semibold tabular-nums",
          emphasis ? "text-3xl text-primary" : "text-2xl",
        )}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ModifierList({ modifiers }: { modifiers: IncentiveModifier[] }) {
  if (modifiers.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No reductions are applied to this award.</p>
    );
  }
  return (
    <ul className="divide-y rounded-lg border">
      {modifiers.map((m) => (
        <li key={m.type} className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
          <span>{m.type}</span>
          {m.points > 0 ? (
            <StatusPill tone="danger" label={`−${formatPercent(m.points)}`} />
          ) : (
            <span className="text-muted-foreground">None</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function KpiTable({ title, blurb, kpis }: { title: string; blurb: string; kpis: IncentiveKpi[] }) {
  return (
    <DetailSection title={title}>
      <p className="mb-3 text-sm text-muted-foreground">{blurb}</p>
      <DataTable
        storageKey={`incentive-kpis:${title}`}
        headers={[
          { label: "KPI" },
          { label: "Weight", className: "text-right" },
          { label: "Min", className: "text-right" },
          { label: "Target", className: "text-right" },
          { label: "Max", className: "text-right" },
          { label: "Actual" },
          { label: "Achieved" },
          { label: "Earned", className: "text-right" },
          { label: "Proj value", className: "text-right" },
          { label: "Proj achieved", className: "text-right" },
          { label: "Proj awarded", className: "text-right" },
        ]}
        empty={
          <EmptyState
            icon={Target}
            title={`No ${title}`}
            description="NOS lists none for you this year."
          />
        }
      >
        {kpis.map((kpi) => (
          <KpiRow key={kpi.key} kpi={kpi} />
        ))}
      </DataTable>
    </DetailSection>
  );
}

/** One end of the target range; a pass/fail KPI has none, and NOS may hold none. */
function band(kpi: IncentiveKpi, value: number | null): string {
  return kpi.isBinary || value === null ? "—" : formatKpiValue(value, kpi);
}

function KpiRow({ kpi }: { kpi: IncentiveKpi }) {
  return (
    <tr>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 font-medium">
          {kpi.name}
          {kpi.definition && (
            <SimpleTooltip content={kpi.definition}>
              <button
                type="button"
                aria-label={`What ${kpi.name} measures`}
                className="text-muted-foreground hover:text-foreground"
              >
                <Info className="size-3.5" aria-hidden />
              </button>
            </SimpleTooltip>
          )}
        </div>
        {kpi.scope && <p className="text-2xs text-muted-foreground">{kpi.scope}</p>}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">{formatPercent(kpi.weight)}</td>
      <td className="px-4 py-3 text-right tabular-nums">{band(kpi, kpi.min)}</td>
      <td className="px-4 py-3 text-right tabular-nums">
        {kpi.isBinary ? (
          <span className="text-muted-foreground">Pass / fail</span>
        ) : (
          formatKpiValue(kpi.target, kpi)
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">{band(kpi, kpi.max)}</td>
      <td className="px-4 py-3 tabular-nums">
        {formatKpiValue(kpi.actual, kpi, kpi.actualDisplay)}
      </td>
      <td className="px-4 py-3">
        <Progress kpi={kpi} />
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        <Earned kpi={kpi} />
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {formatKpiValue(kpi.projected, kpi, kpi.projectedDisplay)}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">{formatPercent(kpi.achievedProjected)}</td>
      <td className="px-4 py-3 text-right tabular-nums">{formatPercent(kpi.awardedProjected)}</td>
    </tr>
  );
}

/** Pass/fail for a binary KPI; otherwise a bar capped at the target with the true % beside it. */
function Progress({ kpi }: { kpi: IncentiveKpi }) {
  if (kpi.achieved === null) return <span className="text-muted-foreground">—</span>;
  if (kpi.isBinary) {
    return kpi.achieved >= 1 ? (
      <StatusPill tone="success" label="Met" />
    ) : (
      <StatusPill tone="danger" label="Not met" />
    );
  }
  const tone = kpi.achieved >= 1 ? "success" : kpi.achieved >= 0.5 ? "warning" : "danger";
  return (
    <div className="flex min-w-36 items-center gap-2">
      <Bar
        fraction={Math.min(1, Math.max(0, kpi.achieved))}
        tone={tone}
        label={`${formatPercent(kpi.achieved, 1)} of target`}
        className="mt-0 flex-1"
      />
      <span className="w-14 text-right text-xs tabular-nums text-muted-foreground">
        {formatPercent(kpi.achieved, 1)}
      </span>
    </div>
  );
}

function Earned({ kpi }: { kpi: IncentiveKpi }) {
  const share = earnedShare(kpi);
  return (
    <>
      <p className="font-medium">{formatPercent(kpi.awarded)}</p>
      {share !== null && (
        <p className="text-2xs text-muted-foreground">of {formatPercent(kpi.weight)}</p>
      )}
    </>
  );
}

const BAR_TONE = {
  primary: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
} as const;

/** A thin progress track. `fraction` is 0–1; the label is for screen readers. */
function Bar({
  fraction,
  tone,
  label,
  className,
}: {
  fraction: number;
  tone: keyof typeof BAR_TONE;
  label: string;
  className?: string;
}) {
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(fraction * 100)}
      className={cn("mt-3 h-2 overflow-hidden rounded-full bg-muted", className)}
    >
      <div
        className={cn("h-full rounded-full", BAR_TONE[tone])}
        style={{ width: `${fraction * 100}%` }}
      />
    </div>
  );
}
